import { useState } from 'react';
import { useIntl } from 'react-intl';

import { getSession } from 'src/contexts/AuthContext';
import ContentManager from 'src/componentes/content-manager';
import Alert from 'src/componentes/message-box/alert';
import useAuth from 'src/hooks/useAuth';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';
import guid from 'src/utils/guid';
import { isNullOrWhiteSpace } from 'src/utils/string';
import { TIPO_INTERACAO_SOPHIE } from 'src/utils/constants';

export default function useGerarResumoIA(entidadeId) {
  const intl = useIntl();
  const { terms: resources } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [resumoGerado, setResumoGerado] = useState(null);
  const [requestIdGerado, setRequestIdGerado] = useState(null);
  const [erro, setErro] = useState(false);

  const gerarResumo = async () => {
    if (!entidadeId || isGenerating) return;

    setIsGenerating(true);
    setErro(false);
    const requestId = guid();

    try {
      const responsePrompt = await http.get(
        '/FormularioGlobalInsights/ObterPrompt',
        {
          params: { entidadeId },
        }
      );

      const { possuiDadosSuficientes, prompt, promptSistema } =
        responsePrompt?.data ?? {};

      const promptCompleto = [promptSistema, prompt]
        .filter((conteudo) => !isNullOrWhiteSpace(conteudo))
        .join('\n\n');

      if (!possuiDadosSuficientes || isNullOrWhiteSpace(promptCompleto)) {
        ContentManager.addContent(
          <Alert
            message={intl.formatMessage(
              { id: 'labelInsightsPesquisaClimaSemDadosSuficientes' },
              { pesquisa: resources.pesquisa }
            )}
          />
        );
        return;
      }

      const { accessToken } = getSession();
      const response = await fetch(
        process.env.REACT_APP_API_OPENAI + '/api/open-ai',
        {
          method: 'POST',
          body: JSON.stringify({
            messages: [
              { role: 'user', content: promptCompleto },
              { role: 'assistant', content: '' },
            ],
            interactionType: TIPO_INTERACAO_SOPHIE.INSIGHTS_PESQUISA_CLIMA,
            requestId,
            hasLicence: true,
          }),
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`OpenAI error: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let fullText = '';
      let done = false;

      while (!done) {
        const { value, done: streamDone } = await reader.read();
        if (value) fullText += decoder.decode(value, { stream: !streamDone });
        done = streamDone;
      }

      fullText += decoder.decode();
      await http.post('/PesquisaClima/SalvarResumoIA', {
        pesquisaClimaId: entidadeId,
        requestId,
        resumoIA: fullText,
      });

      setResumoGerado(fullText);
      setRequestIdGerado(requestId);
    } catch (err) {
      errorHandler(err);
      setErro(true);
    } finally {
      setIsGenerating(false);
    }
  };

  return { isGenerating, erro, resumoGerado, requestIdGerado, gerarResumo };
}
